import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UIRouter }         from '@uirouter/core';

import { CompanyService }          from '../companies/company.service';
import { MessagesService }         from '../messages/messages.service';
import { ProductActionsComponent } from './product.actions.component';
import { ProductModel }            from './typings/product';
import { ProductsFiltersModel }    from './typings/products.filters';
import { ProductsService }         from './products.service';
import { Table }                   from '../table/types/table';
import { TableColumn }             from '../table/types/table-column';
import { TableQueryParameters }    from '../table/types/table-query-parameters';
import { TableService }            from '../table/table.service';

@Component({
  selector   : 'wor-products',
  template: require('./products.component.html')
})
export class ProductsComponent {
  @Input() products: Array<ProductModel> = [];

  filters : ProductsFiltersModel = this.tableService.setFiltersFromCache(this.productsService.tableCacheKey, {
      inactiveOnly: false,
      value       : ''
    });

  tableSettings : Table = {
    broadcast: this.productsService.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.productsService.tableCacheKey
    },
    columns: this.getColumns(),
    empty  : {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PRODUCTS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.productsService.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.productsService.getCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.productsService.tableCacheKey, {
      direction: 'asc',
      field    : 'item_code'
    })
  };

  constructor (
    private companyService  : CompanyService,
    private messagesService : MessagesService,
    private productsService : ProductsService,
    private router          : UIRouter,
    private tableService    : TableService,
    private translateService: TranslateService
  ) {}

  activate ( product : ProductModel ) : void {
    this.productsService.activate(product)
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.PRODUCT_REACTIVATED'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  filterProducts () : void {
    if ( window[ this.productsService.broadcastKey ] ) {
      window[ this.productsService.broadcastKey ](this.filters);
    }
  }

  getColumns () : Array<TableColumn> {
    const columns : Array<TableColumn> = [
      {
        classes: 'all-caps',
        field  : 'item_code',
        header : {
          classes: 'all-caps',
          text   : this.translateService.instant('PRODUCT_SPACE.ITEM_CODE')
        }
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        field : 'brand',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.BRAND')
        }
      },
      {
        classes: 'text-right',
        field  : 'retail_price',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('PRODUCT_SPACE.RETAIL_PRICE')
        },
        pipe: 'globalCurrency'
      },
      {
        classes: 'text-right',
        field  : this.companyService.priceIncludesTax()
            ? 'cost_including_tax'
            : 'cost',
        header: {
          classes: 'text-right',
          text   : this.translateService.instant('PRODUCT_SPACE.COST')
        },
        pipe: 'globalCurrency'
      },
      {
        classes: 'text-right',
        field  : 'quantity_on_hand',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('PRODUCT_SPACE.QTY')
        }
      }
    ];

    if ( this.companyService.hasReservedStock() ) {
      columns.push({
        classes: 'text-right',
        field  : 'qty_available',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('PRODUCT_SPACE.QTY')
        }
      });
    }

    columns.push({
      component: {
        bindings: {
          outputs: {
            onActivateClick: ( product : ProductModel ) => this.activate(product),
            onEditClick    : ( product : ProductModel ) => this.viewProduct(product)
          }
        },
        enabled: true,
        factory: ProductActionsComponent,
      },
      sortable: false,
    });

    return columns;
  }

  getProducts ( params : TableQueryParameters ) : Promise<Array<ProductModel>> {
    return this.productsService.get(params, this.filters)
      .then(( products : Array<ProductModel> ) => {
        this.products = products;

        return products;
      });
  }

  viewProduct ( product : ProductModel ) : void {
    this.router.stateService.go('app.product', {
      id: product.id
    });
  }
}
